import { Skeleton } from 'components/Skeleton'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { BorderTravelOverlay } from '../../../creative/previews/ShimmerOverlay'
import { FAILED_MEDIA_SOURCE, toMediaSource } from '../../../creative/components/AssetMedia'
import { useEditsRenderState } from '../../../creative/components/EditsApplyingBadge'
import {
  normalizeShape,
  toFailedPlatforms,
  toPublishedPlatformNames,
} from '../../../creative/components/post-card-chips'
import { ScheduledPostCard } from '../../../creative/components/ScheduledPostCard'
import type { ContentShape } from '../../../creative/store/idea-store'
import {
  useWeeklyQueueStore,
  type WeeklyQueueItem,
  type WeeklyQueuePendingAction,
} from '../../store/weekly-queue-store'

/**
 * The Weekly Cockpit's thin adapter over the shared `ScheduledPostCard` — every
 * queue item (desktop grid + mobile day strip) renders through this. It maps the
 * weekly-queue DTO → the card's props (deriving media, status booleans, and the
 * publish-pulse) and wires the cockpit's open / edit / delete / approve
 * callbacks. The presentation (clickable media tile, corner chips, platform row
 * + ••• menu, Approve footer) lives in `ScheduledPostCard`, shared with the
 * Calendar's `CalendarPopoverCard`.
 *
 * The card is width-agnostic (`w-full`) — callers own the width (DayDetail wraps
 * at 240px in a snap row; WeekGrid lets it fill the cell).
 */

interface QueuedPostCardProps {
  item: WeeklyQueueItem
  pendingAction?: WeeklyQueuePendingAction
  /** Opens PostModal — the single hub for schedule / approve / view-live. */
  onOpenPost: () => void
  /** Routes to the post-aware Composer (the ••• Edit action). */
  onEdit: () => void
  /** Move a scheduled post back to a draft (the ••• Unschedule action). */
  onUnschedule: () => void
  onDelete: () => void
  /** Approve a review-mode post so it publishes at its scheduled time. */
  onApprove?: () => void
  /** True while the parent is loading this post's PostModal payload. */
  opening?: boolean
  /**
   * Index in the parent's card list. When present, the article becomes
   * keyboard-navigable (DayDetail wires j/k/← /→ + Enter/Space → open the post
   * via `data-card-index`).
   */
  cardIndex?: number
}

export function QueuedPostCard({
  item,
  pendingAction,
  onOpenPost,
  onEdit,
  onUnschedule,
  onDelete,
  onApprove,
  opening,
  cardIndex,
}: QueuedPostCardProps) {
  const isWorking = pendingAction !== undefined
  const isGenerating = item.status === 'generating' || pendingAction === 'regenerate'
  const isPublished = item.status === 'published'
  // A generation failure (the AI never made the post) renders the failed-media
  // plate + Retry, not the publish-failure dim/chip treatment — both arrive as
  // status='failed', the server's `generationFailed` tells them apart.
  const isGenerationFailed = item.generationFailed === true && !isGenerating && !isPublished
  const isFailed = item.status === 'failed' && !isGenerationFailed
  // needs_approval = ready on a review-mode plan, awaiting the user's approval.
  const isNeedsApproval = item.status === 'needs_approval'
  // Scheduled = on the publish rails, not yet live.
  const isScheduled = item.status === 'scheduled'
  // The tile's thumbnail lags the user's composition edits until the master
  // re-renders — chip it on pre-publish posts (published masters are final).
  // item.editsApplying is the server-computed truth (the cockpit payload doesn't
  // hydrate the creative/idea stores); the store hook layers live Pusher updates
  // on top for creatives that ARE hydrated (e.g. after opening the post modal).
  const storeEditsState = useEditsRenderState(item.creativeId)
  const isEditsApplying = (item.editsApplying === true || storeEditsState !== null) && !isPublished
  // Same two sources for the terminal failed state (server flag + live store).
  const isEditsRenderFailed = item.editsRenderFailed === true || storeEditsState === 'failed'

  const shape = normalizeShape(item.shape)

  // Only render the rich (video / carousel) media when its source is actually
  // present on the item — otherwise downgrade to a static image so the card
  // shows a clean thumbnail instead of AssetMedia's eternal "generating" poster
  // (a real cockpit reel/carousel has no videoUrl/mediaItems until the server
  // DTO carries them; the fakes do, and so will real items once it does).
  const hasRichMedia =
    (shape === 'short_vertical_video' && !!item.videoUrl) ||
    (shape === 'carousel' && (item.mediaItems?.length ?? 0) > 0)
  const effectiveShape: ContentShape = hasRichMedia ? shape : 'static_image'
  const hasMedia = hasRichMedia || !!item.thumbnailUrl
  // Build the AssetMedia source only when there's media to render (skips
  // generating + text-only cards), keyed on just the media inputs so the
  // frequent status/schedule churn in the cockpit doesn't rebuild it.
  const mediaSource = useMemo(
    () =>
      hasMedia && !isGenerating
        ? toMediaSource(item)
        : isGenerationFailed
          ? FAILED_MEDIA_SOURCE
          : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only media inputs matter
    [
      hasMedia,
      isGenerating,
      isGenerationFailed,
      item.creativeId,
      item.thumbnailUrl,
      item.videoUrl,
      item.mediaItems,
    ]
  )

  // Retry a failed generation: the store's regenerate flips the tile to
  // 'generating' optimistically and the poll ladder picks up the fresh draft.
  const regenerate = useWeeklyQueueStore(s => s.regenerate)
  const handleRetryGeneration = () => {
    regenerate(item.creativeId).catch(err => {
      console.error('Failed to retry generation:', err)
      toast.error("We couldn't restart this post. Please try again.")
    })
  }

  // Generating posts have nothing to open yet; everything else opens PostModal.
  const isClickable = !isGenerating
  // Edit + Delete only apply before a post goes out — never on a published post.
  const showMenu = isScheduled || isNeedsApproval || isFailed || isGenerationFailed

  // One-shot acid pulse the moment a scheduled post actually goes live — the
  // killer cockpit transition (styled via the `data-just-approved` attribute).
  const [pulseToken, setPulseToken] = useState(0)
  const prevStatusRef = useRef(item.status)
  useEffect(() => {
    const prev = prevStatusRef.current
    const curr = item.status
    if (prev !== 'published' && curr === 'published') setPulseToken(t => t + 1)
    prevStatusRef.current = curr
  }, [item.status])
  useEffect(() => {
    if (pulseToken === 0) return
    const t = setTimeout(() => setPulseToken(0), 1400)
    return () => clearTimeout(t)
  }, [pulseToken])
  const isPulsing = pulseToken > 0

  // Platforms this slot will publish to. Before the user confirms via PostModal,
  // only the primary platform is known; confirmedPlatforms carries the chosen
  // set afterwards. Shown as plain brand icons (no per-platform status rings).
  const platforms =
    item.confirmedPlatforms && item.confirmedPlatforms.length > 0
      ? item.confirmedPlatforms
      : item.primaryPlatform
        ? [item.primaryPlatform]
        : []

  // Per-sibling failure list drives the destructive chip + per-icon dots.
  // Derived from server-emitted platformStatuses so a partial failure (one
  // sibling failed, another live) surfaces without collapsing into the card's
  // top-level status. Absent on legacy_creative — no failure treatment there.
  const failedPlatforms = toFailedPlatforms(item.platformStatuses)
  const publishedPlatforms = toPublishedPlatformNames(item.platformStatuses)

  // Publish-hold honesty: the server marks past-due posts a sweep gate is
  // holding. Copy keys off the reason + the business-wide billing hold so the
  // chip says the true cause, not a generic "stuck". A partially-published
  // post (some platform already live) is NOT "didn't publish" — the platform
  // row tells that story; suppress the chip to avoid contradicting it.
  const billingHold = useWeeklyQueueStore(s => s.billingHold)
  const hold = (() => {
    if (publishedPlatforms.length > 0) return undefined
    if (item.holdReason === 'approval_missed') {
      return {
        label: 'Missed approval',
        title:
          'The scheduled time passed before this post was approved. Approve it and it publishes right away.',
      }
    }
    if (item.holdReason === 'missed') {
      return billingHold
        ? {
            label: 'On hold',
            title:
              'Publishing is paused because this business has no active subscription. Reactivate to resume.',
          }
        : {
            label: "Didn't publish",
            title:
              "This post missed its scheduled time and won't publish on its own. Open it to reschedule.",
          }
    }
    return undefined
  })()

  return (
    <ScheduledPostCard
      testId="queued-post-card"
      mediaSource={mediaSource}
      shape={shape}
      effectiveShape={effectiveShape}
      platforms={platforms}
      isGenerating={isGenerating}
      isPublished={isPublished}
      isFailed={isFailed}
      isNeedsApproval={isNeedsApproval}
      showMenu={showMenu}
      isClickable={isClickable}
      opening={opening}
      onOpen={onOpenPost}
      onEdit={onEdit}
      onUnschedule={isScheduled ? onUnschedule : undefined}
      onConfirmDelete={onDelete}
      onApprove={onApprove}
      onRetryGeneration={isGenerationFailed ? handleRetryGeneration : undefined}
      deleteDialogTitle="Delete this post?"
      deleteDialogDescription="This permanently deletes the post. It won’t publish, and the slot won’t be refilled."
      failedPlatforms={failedPlatforms}
      publishedPlatforms={publishedPlatforms}
      busy={isWorking}
      cardIndex={cardIndex}
      dataStatus={item.status}
      isPulsing={isPulsing}
      isEditsApplying={isEditsApplying}
      editsCreativeId={item.creativeId}
      editsRenderFailed={isEditsRenderFailed}
      holdLabel={hold?.label}
      holdTitle={hold?.title}
    />
  )
}

/**
 * Image-first skeleton shaped like a generating QueuedPostCard body, used by
 * DayDetail's optimistic "Drafting…" placeholder so the swap when the real
 * creative arrives is visually invisible.
 */
export function PostSkeleton() {
  return (
    <div className="relative flex flex-col overflow-hidden bg-bg-2">
      <BorderTravelOverlay />
      <div className="flex items-center gap-2 px-2.5 py-1.5">
        <Skeleton variant="dark" className="h-6 w-6 shrink-0 rounded-full" />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Skeleton variant="dark" className="h-2.5 w-20 rounded" />
          <Skeleton variant="dark" className="h-2 w-14 rounded" />
        </div>
      </div>
      <Skeleton variant="dark" className="aspect-[4/3] w-full rounded-none" />
      <div className="flex flex-col gap-1.5 px-2.5 pt-1.5 pb-2">
        <div className="flex gap-1.5">
          <Skeleton variant="dark" className="h-2.5 w-12 rounded" />
          <Skeleton variant="dark" className="h-2.5 w-28 rounded" />
        </div>
        <Skeleton variant="dark" className="h-2.5 w-[80%] rounded" />
      </div>
    </div>
  )
}
